import { useCallback, useEffect, useRef } from "preact/hooks";
import type { RefObject } from "preact";
import { useEscape } from "./escape";
import { trapFocus } from "./focus-trap";

// Split out of DevSheet.tsx (T10, docs/performance.md C3) so the entry chunk
// does not have to import the whole workshop module just for the logo-tap
// detector that opens it. DevSheet.tsx itself keeps its own copies (nothing
// else in that file needs this module), so the two stay in sync only by the
// string/behaviour staying simple; if either changes, change both.

/** Dispatched on window when the workshop should open (five taps on the logo). */
export const DEV_EVENT = "op:dev";
const TAPS_NEEDED = 5;
const TAP_WINDOW_MS = 2000;

let taps: number[] = [];
/** Call from the logo's click handler; fires the dev event on five quick taps. */
export function registerLogoTap(): void {
  const now = Date.now();
  taps = taps.filter((t) => now - t < TAP_WINDOW_MS);
  taps.push(now);
  if (taps.length >= TAPS_NEEDED) {
    taps = [];
    window.dispatchEvent(new CustomEvent(DEV_EVENT));
  }
}

// The sheets' and prompts' shared layer behaviour lives here too: this module is already in the
// entry chunk, and the hook is small. It is not the workshop's, it is every sheet's.

/**
 * A sheet or prompt as a layer: Escape (and Android back, through the same stack in src/escape.ts)
 * runs `onClose`, the same close the backdrop runs, so the falling cue plays once. On open the
 * focus moves into the layer (its autofocus field or a `data-autofocus` control, else the dialog
 * itself, which needs tabIndex -1), Tab and Shift+Tab cycle inside it and the page behind is inert
 * (src/focus-trap.ts); on close the trap lifts and the focus goes back to whatever had it before.
 * Returns the ref to put on the dialog element.
 */
export function useSheetLayer<T extends HTMLElement = HTMLDivElement>(active: boolean, onClose: () => void): RefObject<T> {
  const ref = useRef<T>(null);
  const latest = useRef(onClose);
  latest.current = onClose;
  // Stable, so a re-render does not re-register the layer and move it to the top of the stack.
  const escape = useCallback(() => latest.current(), []);
  useEscape(active, escape);
  useEffect(() => {
    if (!active) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = ref.current;
    const release = el ? trapFocus(el) : () => {};
    if (el && !el.contains(document.activeElement)) {
      const auto = el.querySelector<HTMLElement>("[autofocus], [data-autofocus]");
      (auto ?? el).focus({ preventScroll: true });
    }
    return () => {
      // The trap lifts first: an inert opener cannot take the focus back.
      release();
      if (before && before.isConnected && before !== document.body) before.focus({ preventScroll: true });
    };
  }, [active]);
  return ref;
}
